import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Card, CardContent, Typography, Box, Chip } from '@material-ui/core';
import { CalendarToday } from '@material-ui/icons';
import Image from '../common/Image';
import formatDate from '../../utils/formatDate';
import TagCloud from '../common/TagCloud';
import { CategoryTag, EnglisTag, WorkTypeTag, AreaTag } from './Tags';


const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[100]}`,
        borderRadius: '5px',
        padding: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
        transition: 'all 0.4s ease',
        cursor: 'pointer',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '1px 1px 2px rgba(0,0,0,0.4)',
        },
    },
    title: {
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.dark,
        lineHeight: 1.3,
    },
    content: {
        padding: 0,
    },
    user: {
        display: 'flex',
        marginBottom: theme.spacing(0.5),
    },
    userImage: {
        width: '15rem',
        height: '15rem',
        marginRight: theme.spacing(1),
        flexShrink: 0,
        '& img': {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
        }
    },
    userInfo: {
        flex: 1,
    },
    area: {
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1),
        fontWeight: 500,
        fontSize: '1.6rem',
        fontFamily: '"Kosugi Maru", sans-serif',
        textDecoration: 'none !important',
    },
    dateContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1)
    },
    tagContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2)
    },
    tagCloud: {
        marginBottom: theme.spacing(2),
    },
    description: {
        padding: theme.spacing(1),
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
        textDecoration: 'none !important',
    },
    marginBottomSmall: {
        marginBottom: '1.5rem !important',
    },
    marginTopSmall: {
        marginTop: '1.5rem !important',
    },
    flex: {
        display: 'flex',
    },
    flexWrap: {
        flexWrap: 'wrap',
    },
    flexAlign: {
        alignItems: 'center',
    },
    textWrap: {
        whiteSpace: 'pre-wrap',
    },
}));

export default function JobCard({ job, saved, applied }) {
    const classes = useStyles();
    let jobDescription = job.details.length > 200 ? job.details.substr(0, 200) + '...' : job.details;
    let imageUrl = job.mainImage
        ? job.mainImage.image_url
        : (
            job.user.profile.avatar
                ? job.user.profile.avatar.image_url
                : null
        );

    const renderJobImage = () => {
        if (!imageUrl) return null;
        return (
            <Box className={classes.userImage}>
                <Image src={imageUrl} size={'200'} alt='Job image' />
            </Box>
        )
    }

    const renderEnglishLevel = englishLevel => {
        let content = englishLevel ? englishLevel : '不問';
        return <EnglisTag content={content} size="small" />
    }

    return (
        <Card className={classes.root}>
            <Typography variant="h3" component="h3" className={classes.title}>
                {job.title}
            </Typography>
            <CardContent className={classes.content}>
                <Box className={classes.left}>
                    <Box className={classes.user}>
                        {renderJobImage()}
                        <Box className={classes.userInfo}>
                            <AreaTag content={job.area ? job.area.name : null} size="small" />
                            <Typography variant="body2" gutterBottom>
                                投稿者:{job.user.name}
                            </Typography>
                            <Box className={classes.dateContainer}>
                                <CalendarToday fontSize="small" />
                                <Typography variant="body2">
                                    {formatDate(job.updated_at)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box className={`${classes.tagContainer} ${classes.flex} ${classes.flexWrap}`}>
                        <CategoryTag content={job.jobCategory} size="small" />
                        {renderEnglishLevel(job.englishLevel)}
                        {job.workType && (
                            <WorkTypeTag content={job.workType} size="small" />
                        )}
                    </Box>
                    <Box className={classes.tagCloud}>
                        <TagCloud tags={job.tags} />
                    </Box>
                </Box>
                <Typography variant="body2" className={classes.description}>
                    {jobDescription}
                </Typography>
            </CardContent>
        </Card>
    );
}
